import React, { useState, useEffect } from "react";
import ForwardedNumericFormat from "@/components/atoms/ForwardedNumericFormat";
import Button from "@/components/atoms/Button";
import axios from "axios";
import { toast } from "react-toastify";

interface SowAmount {
  sow_id: string;
  period: string;
  amount: number;
}

interface SowAmountsProps {
  data: SowAmount[];
  invoiceId: number;
  onUpdateSowAmounts: (newValue: SowAmount[]) => void;
}

interface GroupedData {
  [sowId: string]: SowAmount[];
}

interface UpdatedData {
  [sowId: string]: {
    [period: string]: {
      amount: number;
    };
  };
}

const SowsAmountsTab: React.FC<SowAmountsProps> = ({ data, invoiceId, onUpdateSowAmounts }) => {
  const [groupedData, setGroupedData] = useState<GroupedData>({});
  const [updatedData, setUpdatedData] = useState<UpdatedData>({});
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const grouped = data.reduce<GroupedData>((acc, item) => {
      if (!acc[item.sow_id]) acc[item.sow_id] = [];
      acc[item.sow_id].push(item);
      return acc;
    }, {});
    setGroupedData(grouped);
  }, [data]);
  

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  const handleInputChange = (
    sowId: string,
    period: string,
    value: number
  ) => {
    setUpdatedData((prev) => {
      const updated = { ...prev };
  
      Object.keys(groupedData).forEach((currentSowId) => {
        updated[currentSowId] = {
          ...updated[currentSowId],
          ...Object.fromEntries(
            groupedData[currentSowId].map((item) => [
              item.period,
              {
                amount:
                  currentSowId === sowId && item.period === period
                    ? value
                    : updated[currentSowId]?.[item.period]?.amount ?? item.amount,
              },
            ])
          ),
        };
      });
  
      return updated;
    });
  };
  
  const handleSave = async () => {
    if (loading || Object.keys(updatedData).length === 0) return;

    setLoading(true);

    const payload = Object.entries(updatedData).flatMap(([sowId, amounts]) =>
      Object.entries(amounts).map(([period, data]) => ({
        sow_id: sowId,
        period,
        amount: data.amount,
      }))
    );

    const csrfToken = getCsrfToken();

    try {
      const response = await axios.post(
        `/auto_invoices/${invoiceId}/update_sow_amounts`,
        { auto_invoice: { sow_amounts: payload } },
        {
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        onUpdateSowAmounts(payload);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatServicePeriod = (period: string) => {
    const year = period.slice(0, 4);
    const month = period.slice(4, 6);
    const date = new Date(`${year}-${month}-01`);
    return date.toLocaleString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '/');
  };

  return (
    <div className="p-4">
      {Object.entries(groupedData).map(([sowId, amounts]) => (
        <div key={sowId} className="mb-6">
          <h3 className="font-bold text-lg mb-2">Sow ID: {sowId}</h3>
          <div className="flex gap-4">
            {amounts.map((amount: SowAmount) => (
              <div key={amount.period} className="border p-4 rounded">
                <label className="block font-bold mb-1">{formatServicePeriod(amount.period)}</label>
                <ForwardedNumericFormat
                  value={updatedData[sowId]?.[amount.period]?.amount || amount.amount}
                  onValueChange={(values) => {
                    const { value } = values;
                    handleInputChange(sowId, amount.period, parseFloat(value));
                  }}
                  thousandSeparator=","
                  decimalSeparator="."
                  fixedDecimalScale
                  decimalScale={2}
                  placeholder="0.00"
                  className="border p-1 w-full text-right"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button onClick={handleSave} disabled={loading || Object.keys(updatedData).length === 0} type="button">
        {loading ? 'Loading...' : 'Save'}
      </Button>
    </div>
  );
};

export default SowsAmountsTab;
